import React, { useEffect, useState } from 'react';
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Card,
  Alert,
} from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Toast from 'react-hot-toast'; // or use 'react-toastify'
import { saveShippingAddress } from '../slices/cartSlice';
import {
  useGetUserDetailsQuery,
  useUpdateUserAddressesMutation,
  useGetAreasQuery,
} from '../slices/userApiSlice';

const ShippingScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { shippingAddress } = useSelector((state) => state.cart);
  const { userInfo } = useSelector((state) => state.auth);

  const { data: userProfile } = useGetUserDetailsQuery(userInfo._id);
  const [updateUserAddresses] = useUpdateUserAddressesMutation();
  const {
    data: areaData,
    isLoading: areaLoading,
    error: areaError,
  } = useGetAreasQuery();

  const [selectedIndex, setSelectedIndex] = useState(null);
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [pincode, setPincode] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [area, setArea] = useState('');

  const addresses = userProfile?.addresses || [];

  useEffect(() => {
    if (shippingAddress && selectedIndex === null) {
      setAddress(shippingAddress.address || '');
      setCity(shippingAddress.city || '');
      setPincode(shippingAddress.pincode || '');
      setPhoneNumber(shippingAddress.phoneNumber || '');
      setArea(shippingAddress.area || '');
    }
  }, [shippingAddress]);

  const selectSavedAddress = (index) => {
    setSelectedIndex(index);
    const selected = addresses[index];
    setAddress(selected.address);
    setCity(selected.city);
    setPincode(selected.pincode);
    setPhoneNumber(selected.phoneNumber || '');
    setArea(selected.area || '');
  };

  const useNewAddress = () => {
    setSelectedIndex(null);
    setAddress('');
    setCity('');
    setPincode('');
    setPhoneNumber('');
    setArea('');
  };

  const submitHandler = async (e) => {
    e.preventDefault();

    if (!address || !city || !pincode || !phoneNumber || !area) {
      Toast.error('Please fill out all fields including area.');
      return;
    }

    const shippingData = { address, city, pincode, phoneNumber, area };
    dispatch(saveShippingAddress(shippingData));

    try {
      if (selectedIndex === null) {
        await updateUserAddresses({
          addresses: [
            ...addresses,
            {
              address,
              city,
              pincode,
              phoneNumber,
              area,
            },
          ],
        }).unwrap();
      }

      navigate('/placeorder');
    } catch (error) {
      Toast.error(error?.data?.message || 'Failed to save address');
    }
  };

  return (
    <Container className="py-4">
      <h2 className="mb-4">Select a Shipping Address</h2>

      {addresses.length > 0 ? (
        <>
          <Row className="mb-3">
            {addresses.map((addr, index) => (
              <Col md={6} lg={4} key={index}>
                <Card
                  className={`mb-3 ${
                    selectedIndex === index ? 'border-primary' : ''
                  }`}
                  onClick={() => selectSavedAddress(index)}
                  style={{ cursor: 'pointer' }}
                >
                  <Card.Body>
                    <Card.Text>
                      {addr.address}, {addr.city} - {addr.pincode}
                    </Card.Text>
                    <Card.Text>📞 {addr.phoneNumber}</Card.Text>
                    {addr.area && <Card.Text>📍 {addr.area}</Card.Text>}
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
          <Button variant="link" onClick={useNewAddress}>
            + Use a new address
          </Button>
        </>
      ) : (
        <Alert variant="info">No saved addresses found.</Alert>
      )}

      <h4 className="mt-4">Shipping Details</h4>

      <Form onSubmit={submitHandler}>
        <Form.Group controlId="address" className="mb-3">
          <Form.Label>Address</Form.Label>
          <Form.Control
            type="text"
            placeholder="Enter address"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
          />
        </Form.Group>

        <Form.Group controlId="city" className="mb-3">
          <Form.Label>City</Form.Label>
          <Form.Control
            type="text"
            placeholder="Enter city"
            value={city}
            onChange={(e) => setCity(e.target.value)}
          />
        </Form.Group>

        <Form.Group controlId="pincode" className="mb-3">
          <Form.Label>Postal Code</Form.Label>
          <Form.Control
            type="text"
            placeholder="Enter postal code"
            value={pincode}
            onChange={(e) => setPincode(e.target.value)}
          />
        </Form.Group>

        <Form.Group controlId="phoneNumber" className="mb-3">
          <Form.Label>Phone Number</Form.Label>
          <Form.Control
            type="tel"
            placeholder="Enter phone number"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
          />
        </Form.Group>

        <Form.Group controlId="area" className="mb-4">
          <Form.Label>Area</Form.Label>
          {areaLoading ? (
            <div>Loading areas...</div>
          ) : areaError ? (
            <Alert variant="danger">Failed to load area list</Alert>
          ) : (
            <Form.Select
              value={area}
              onChange={(e) => setArea(e.target.value)}
            >
              <option value="">Select Area</option>
              {areaData?.areas?.map((areaItem) => (
                <option key={areaItem} value={areaItem}>
                  {areaItem}
                </option>
              ))}
            </Form.Select>
          )}
        </Form.Group>

        <Button type="submit" variant="primary">
          Continue
        </Button>
      </Form>
    </Container>
  );
};

export default ShippingScreen;
